import { useState, useEffect, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { showToast } from '../../components/Toast';

interface User {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: string;
  active: number;
}

export function UserManager() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', role: 'parent' });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const loadUsers = async () => {
    try {
      const res = await api.getUsers();
      setUsers(res.users || []);
    } catch { showToast('Failed to load users'); } finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, []);

  const resetForm = () => {
    setForm({ name: '', email: '', password: '', phone: '', role: 'parent' });
    setEditingId(null);
    setShowForm(false);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError('Name and email are required');
      return;
    }
    if (!editingId && !form.password) {
      setError('Password is required for new users');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingId) {
        const updateData: Record<string, unknown> = {
          name: form.name,
          phone: form.phone || null,
          role: form.role,
        };
        if (form.password) updateData.password = form.password;
        await api.updateUser(editingId, updateData);
        setSuccess(`User ${form.email} updated`);
      } else {
        await api.register({
          name: form.name,
          email: form.email,
          password: form.password,
          phone: form.phone || undefined,
          role: form.role,
        });
        setSuccess(`User ${form.email} created as ${form.role}`);
      }
      resetForm();
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (u: User) => {
    setForm({ name: u.name, email: u.email, password: '', phone: u.phone || '', role: u.role });
    setEditingId(u.id);
    setShowForm(true);
    setError(null);
    setSuccess(null);
  };

  const filteredUsers = filter === 'all' ? users : users.filter(u => u.role === filter);

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div>
      <div className="admin-section-header">
        <h2>User Management ({users.length} users)</h2>
        <button className="btn-sm btn-primary" onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}>
          {showForm ? 'Cancel' : '+ Create User'}
        </button>
      </div>

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit} aria-label={editingId ? 'Edit user form' : 'Create user form'}>
          <div className="admin-form-row">
            <div className="form-group">
              <label>Full Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" required aria-label="Full name" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="user@school.com" required disabled={!!editingId} aria-label="Email address" />
            </div>
            <div className="form-group">
              <label>{editingId ? 'New Password (leave blank to keep)' : 'Password'}</label>
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editingId ? 'Leave blank to keep' : 'Min 6 chars'} required={!editingId} aria-label="Password" />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="form-group">
              <label>Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91-9876543210" aria-label="Phone number" />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} aria-label="User role">
                <option value="parent">Parent</option>
                <option value="driver">Driver</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          {error && <div className="form-error">{error}</div>}
          {success && <div className="form-success">{success}</div>}
          <button type="submit" className="btn-sm btn-primary" disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Update User' : 'Create User'}
          </button>
          {editingId && (
            <button type="button" className="btn-sm btn-outline" style={{ marginLeft: 8 }} onClick={resetForm}>Cancel Edit</button>
          )}
        </form>
      )}

      {/* Filter tabs */}
      <div className="tab-bar" style={{ marginBottom: 16 }}>
        {['all', 'parent', 'driver', 'admin'].map(role => (
          <button key={role} className={`tab-btn ${filter === role ? 'active' : ''}`} onClick={() => setFilter(role)} aria-label={`Filter by ${role}`} aria-pressed={filter === role}>
            {role === 'all' ? `All (${users.length})` : `${role.charAt(0).toUpperCase() + role.slice(1)}s (${users.filter(u => u.role === role).length})`}
          </button>
        ))}
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Role</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((u) => (
            <tr key={u.id}>
              <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#888' }}>{u.id}</td>
              <td><strong>{u.name}</strong></td>
              <td>{u.email}</td>
              <td>{u.phone || '--'}</td>
              <td>
                <span className={`admin-badge ${u.role === 'admin' ? 'badge-blue' : u.role === 'driver' ? 'badge-green' : 'badge-red'}`}>
                  {u.role}
                </span>
              </td>
              <td>
                <span className={`admin-badge ${u.active ? 'badge-green' : 'badge-red'}`}>
                  {u.active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                <button className="btn-sm btn-outline" onClick={() => startEdit(u)} aria-label={`Edit user ${u.name}`}>Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredUsers.length === 0 && <div className="admin-empty">No users found</div>}
    </div>
  );
}
